import { SupportTicketFilterNames } from "@redotech/effect-api-shared/schema/support-ticket-filters";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import type { AdvancedFilterData } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { AdvancedFilterType } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import type { AdvancedTableFilter } from "@redotech/redo-web/advanced-filters/advanced-filter";
import { deepCopyObject } from "@redotech/util/object";
import { useLiveQuery } from "@tanstack/react-db";
import { useQueryClient } from "@tanstack/react-query";
import { useContext, useMemo, useRef, useState } from "react";
import { useAccessibleSupportTeams } from "../hooks/use-accessible-support-teams";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";
import { getSupportTicketAdvancedFilters } from "./get-support-ticket-advanced-filters";

/** Loads advanced-filter definitions wired to their live data (tags, teams, users). */
export function useSupportTicketAdvancedFilters(): AdvancedTableFilter[] {
  const collections = useGlobalSupportTicketCollections();
  const platformRpc = useRequiredContext(RedoPlatformAuthRpcClientContext);
  const effectApi = useRequiredContext(EffectApiClientContext);
  const queryClient = useQueryClient();
  const { data: tags } = useLiveQuery(
    (q) => q.from({ tag: collections.tags }),
    [collections.tags],
  );
  const { data: users } = useLiveQuery(
    (q) => q.from({ user: collections.users }),
    [collections.users],
  );
  const accessibleTeams = useAccessibleSupportTeams();
  const team = useContext(TeamContext);
  const teamId = team?._id;
  return useMemo(
    () =>
      getSupportTicketAdvancedFilters({
        effectApi,
        platformRpc,
        queryClient,
        tags,
        accessibleTeams,
        users,
        teamId,
      }),
    [effectApi, platformRpc, queryClient, tags, accessibleTeams, users, teamId],
  );
}

/** Augments filter definitions with "Create tag" injection + modal state. */
export function useSupportTicketFilterDefinitionsWithCreate(
  onBeforeOpen?: () => void,
): {
  allFilterDefinitionsWithCreate: AdvancedTableFilter[];
  createTagModalOpen: boolean;
  setCreateTagModalOpen: (open: boolean) => void;
} {
  const allFilterDefinitions = useSupportTicketAdvancedFilters();
  const { t } = useTranslation();
  const [createTagModalOpen, setCreateTagModalOpen] = useState(false);
  const onBeforeOpenRef = useRef(onBeforeOpen);
  onBeforeOpenRef.current = onBeforeOpen;

  const allFilterDefinitionsWithCreate = useMemo(
    () =>
      allFilterDefinitions.map((f) =>
        f.type === AdvancedFilterType.ARRAY_TO_STRING &&
        f.data.name === SupportTicketFilterNames.SUPPORT_TICKET_TAGS
          ? {
              ...f,
              onRequestCreateItem: () => {
                onBeforeOpenRef.current?.();
                setCreateTagModalOpen(true);
              },
              createItemText: () =>
                t("Create tag", {
                  context: "support ticket tag filter create action",
                }),
            }
          : f,
      ),
    [allFilterDefinitions, t],
  );

  return {
    allFilterDefinitionsWithCreate,
    createTagModalOpen,
    setCreateTagModalOpen,
  };
}

/**
 * Resolves committed filter data back to its full table-filter definition
 * by matching on the filter name.
 */
export function dataToTableFilter(
  data: AdvancedFilterData,
  allFilters: AdvancedTableFilter[],
): AdvancedTableFilter | null {
  const matchingFilter = allFilters.find((f) => f.data.name === data.name);
  if (!matchingFilter) {
    return null;
  }
  const filterCopy = deepCopyObject(matchingFilter);
  filterCopy.data = data;
  return filterCopy;
}
